import { Dispatch } from 'redux';

import { get, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import CustomAppTypes from '../config/customAppTypes';
import { AppState } from '../reducers';
import { customFailed } from '../util/navUtil';
import parseUrlParams from '../util/urlParams';
import { changeSoknadField } from './application';
import { getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';

type Action<T> = import('./types').Action<T>;

export type FetchStatusResponse = import('../generated-types').SoknadStatus;

export interface FetchStatusStartedAction extends Action<typeof actionTypes.FETCH_STATUS_STARTED> {
    type: typeof actionTypes.FETCH_STATUS_STARTED;
}

export interface FetchStatusFulfilledAction extends Action<typeof actionTypes.FETCH_STATUS_FULFILLED> {
    type: typeof actionTypes.FETCH_STATUS_FULFILLED;
    payload: {
        response: FetchStatusResponse;
        paramType?: string;
        paramIncrease?: boolean;
    };
}

export interface FetchStatusFailedAction extends Action<typeof actionTypes.FETCH_STATUS_FAILED> {
    type: typeof actionTypes.FETCH_STATUS_FAILED;
    payload: RequestExceptionWithResponse;
}

export type CheckStatusActions = FetchStatusStartedAction | FetchStatusFulfilledAction | FetchStatusFailedAction;

export const fetchStatusStarted = (): FetchStatusStartedAction => ({
    type: actionTypes.FETCH_STATUS_STARTED,
});
export const fetchStatusFulfilled = (
    json: FetchStatusResponse,
    paramType?: string,
    paramIncrease?: boolean,
): FetchStatusFulfilledAction => ({
    type: actionTypes.FETCH_STATUS_FULFILLED,
    payload: {
        response: json,
        paramType,
        paramIncrease,
    },
});
export const fetchStatusFailed = (error: RequestExceptionWithResponse): FetchStatusFailedAction => ({
    type: actionTypes.FETCH_STATUS_FAILED,
    payload: error,
});

export const fetchStatus = async (
    dispatch: Dispatch<AppActions>,
    getState: () => AppState,
): Promise<FetchStatusResponse | null> => {
    const { loader, context, lbs, feature, cards } = getState();
    const { hasFetchedStatus, isFetchingStatus } = loader;
    const lbsBnr = CustomAppTypes.isLbs(context) && lbs.lbsBankregnr !== null ? `/${lbs.lbsBankregnr}` : '';

    const { hasGold, hasBusiness, korttype, goldCard, businessCard } = cards;

    if (!hasFetchedStatus && !isFetchingStatus) {
        try {
            dispatch(fetchStatusStarted());
            const response: FetchStatusResponse = await get(
                `${actionTypes.RESOURCE_PATH}/application/status${lbsBnr}`,
                getRequestHeaders(context.config.bankregnr),
            );

            if (hasGold && korttype === goldCard.produkt.kode) dispatch(changeSoknadField('kortsaldo', goldCard.saldo));
            else if (hasBusiness && korttype === businessCard.produkt.kode)
                dispatch(changeSoknadField('kortsaldo', businessCard.saldo));
            const routeParams = parseUrlParams();
            if (response.kycBlocked && !feature.kredittkortSblKycBlockedDisabled) {
                dispatch(fetchStatusFailed(customFailed('kyc_blocked_error')));
            } else {
                dispatch(fetchStatusFulfilled(response, routeParams.type, routeParams.endre || undefined));
            }
            return response;
        } catch (error) {
            dispatch(fetchStatusFailed(error));
        }
    }
    return null;
};
